import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Highcharts, { PointOptionsObject } from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { Result } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dayjs } from 'dayjs'
import { ReportRow } from '../../redux/context/reports/types'
import { getDrilldownData } from './utils'
import { useBackend } from '../../services/backend'
import { contextCompanyIdSelector } from '../../redux/context/company/selectors'
import { filtersSelector } from '../../redux/context/filters/selectors'
import { FiscalYear } from '../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../types/periodGroup/PeriodGroup'
import { formatter } from '../../pages/reporting/custom/components/utils'
import { DrillData } from '../../pages/reporting/custom/components/chart/chartUtils'

interface PieChartProps {
  titleText: string
  seriesData: PointOptionsObject[]
  seriesName?: string
  currentPeriodGroup?: FiscalYear | PeriodGroup
  height?: number
  width?: number | null
  percentage?: boolean
  date?: Dayjs | null
  drilldown?: boolean
}

export interface ReportRowExplanation extends ReportRow {
  explanation?: true
  account?: boolean
  period?: InterpreterPeriod
  translation?: string
  source: string
  children?: ReportRowExplanation[]
}

export enum InterpreterPeriod {
  current = 'current',
  previous = 'previous',
  cumulative = 'cumulative',
  pastyear = 'pastyear'
}

export const PieChart: React.FC<PieChartProps> = ({
  titleText,
  seriesData,
  seriesName,
  height = 400,
  width = null,
  percentage,
  date,
  drilldown,
  currentPeriodGroup
}: PieChartProps) => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const chartRef = useRef<HighchartsReact.RefObject>(null)
  const { periodGroups, dimensions, budgetingScenario } = useSelector(filtersSelector)
  const [explainerData, setExplainerData] = useState<DrillData>({})
  // Kept in sync with explainerData state, but read inside the drilldown handler
  // instead of the state value directly. options is memoized below (like
  // CustomPieChart) so it doesn't get recreated on every explainerData update -
  // otherwise HighchartsReact would re-run chart.update(options, true, true) right
  // after a drilldown and wipe out the just-added drilldown series. That memoization
  // means the handler closure is frozen at first creation, so it must read the
  // latest explainer data via a ref rather than the state variable.
  const explainerDataRef = useRef<DrillData>(explainerData)
  const explainFormulaRequest = useBackend('/api/companies/{companyId}/reporting/key-figure-statement/internal/explain')

  // Bumped whenever the period context changes, so an in-flight explain request whose
  // response arrives after the user has moved on to a different period/dimension/scenario
  // can tell its result is stale and skip writing it into explainerDataRef.
  const periodContextRef = useRef(0)

  useEffect(() => {
    // Tyhjää explainer datas jos vaihtuu dimensio, scenario, ajanjakso tai päivämäärä
    explainerDataRef.current = {}
    setExplainerData({})
    periodContextRef.current += 1
  }, [dimensions, budgetingScenario, currentPeriodGroup, date])

  const updateExplainerData = (newData: DrillData) => {
    const updated = { ...explainerDataRef.current, ...newData }
    explainerDataRef.current = updated
    setExplainerData(updated)
  }

  const handleExplainRequest = async (formulaId: number | string) => {
    return explainFormulaRequest.get({
      urlParams: { companyId },
      body: { params: { formulaId, periodGroups } }
    }) as Promise<{ data: ReportRowExplanation[] }[]>
  }

  const drilldownHanlder: Highcharts.DrilldownCallbackFunction = useCallback(
    async e => {
      const chart = chartRef.current?.chart
      if (!e.seriesOptions && chart) {
        const formulaId: string = (e.point as any).drilldown.toString()
        let data: Highcharts.PointOptionsObject[]

        // Jos on eka id, ja explainia ei ole vielä haetaan data asynccinä,
        // muuten otetaan explain statesta
        if (formulaId.split('-').length === 1 && !(formulaId in explainerDataRef.current)) {
          const requestPeriodContext = periodContextRef.current
          chart.showLoading('Loading ...')
          try {
            const explain = await handleExplainRequest(formulaId)

            if (requestPeriodContext !== periodContextRef.current) {
              // Period/dimension/scenario changed while this request was in flight -
              // discard the result instead of restoring drilldown points for a period
              // that's no longer showing.
              return
            }

            const drillData = getDrilldownData(formulaId, explain[0].data, currentPeriodGroup, date)
            updateExplainerData(drillData)
            data = drillData[formulaId]
          } finally {
            chart.hideLoading()
          }
        } else {
          data = explainerDataRef.current[formulaId]
        }
        const series = {
          name: e.point.name,
          id: formulaId,
          type: 'pie',
          data: data?.filter(d => d?.y !== null)
        } satisfies Highcharts.SeriesOptionsType

        chart.addSeriesAsDrilldown(e.point, series)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, periodGroups, currentPeriodGroup, date]
  )

  const options = useMemo(
    () =>
      ({
        chart: {
          plotShadow: false,
          type: 'pie',
          height,
          width,
          events: drilldown && {
            drilldown: drilldownHanlder
          }
        },
        title: {
          text: titleText
        },
        tooltip: {
          formatter
        },
        plotOptions: {
          pie: {
            animation: false,
            allowPointSelect: false,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        series: [
          {
            type: 'pie',
            name: seriesName,
            data: seriesData.map(s => ({ ...s, percent: percentage }))
          }
        ],
        exporting: {
          enabled: false
        }
      } as Highcharts.Options),
    [height, width, drilldown, drilldownHanlder, titleText, seriesName, seriesData, percentage]
  )

  // When seriesData/date genuinely change (as opposed to a drilldown click, which only
  // touches explainerData and is excluded from the options memo above), force a full
  // chart remount rather than relying on HighchartsReact's automatic oneToOne update.
  // The oneToOne update correctly swaps the series back to the top-level data, but it
  // doesn't clear Highcharts' internal drilldownLevels stack or the breadcrumbs UI, so
  // the breadcrumb trail stays stuck on the old drill path even though the pie itself
  // is back to "Main". Remounting gives a fresh chart instance with empty drilldown
  // state, and (unlike manually calling chart.drillUp()) can't double-destroy a series.
  const [chartKey, setChartKey] = useState(0)
  useEffect(() => {
    setChartKey(key => key + 1)
  }, [date, seriesData])

  if (seriesData.length < 1) {
    return <Result status={404} subTitle={t('global:no-data-available')} />
  }

  return <HighchartsReact key={chartKey} ref={chartRef} highcharts={Highcharts} options={options} />
}
